import { clamp } from '@suntex.core/math';

import type { RulesPanelCopy } from '../core/rulesPanelCopy';

import './VifRulesPanel.scss';

/**
 * The canvas badge is a two-frame clip: frame 1 is the plus, frame 2 the
 * minus, switched by `onRulesFramesSet` for the subtraction variants (and by
 * `onSetupGameHook` for mul/div's operator icon). A true minus/multiplication
 * sign rather than ASCII punctuation, to match the artwork's weight.
 */
const BADGE_GLYPH: Record<RulesPanelCopy['operation'], string> = {
  add: '+',
  subtract: '−',
  multiply: '×',
  divide: '÷',
};

/**
 * What the panel shows above its meters. The 4x4 block board's stage
 * (`VifBlockGameStage`) has no `rulesclip`, so it supplies only the operation
 * for the badge and the rule text is left out.
 */
export type VifPanelCopy = RulesPanelCopy | Pick<RulesPanelCopy, 'operation'>;

export interface VifRulesPanelProps {
  copy: VifPanelCopy;
  /** Current round / total, drives the `round` meter. */
  round: number;
  roundsInModule: number;
  /**
   * Current layer / total, drives the `game` meter.
   *
   * The canvas `gmeter` is set to `intLayer` plus a frame offset chosen by the
   * layer count - `+3` for the four-layer artwork, `+8` for the two-layer one
   * (`games/VIFRulesAddSub.ts:590-594`) - so despite the name it tracks the
   * layer within a game, not `gameFrame.game`. Driving it from the game number
   * left it pinned full for every one-game-per-round variant, which is all but
   * flag 5.
   */
  layer: number;
  layersInGame: number;
  /** Seconds remaining, or `null` when the variant is untimed. */
  secondsLeft: number | null;
  /** Whether the START button is showing (board not yet running). */
  canStart: boolean;
  onStart: () => void;
  /**
   * Preview only: reopens the practice menu. Legacy `changeRoundBtn`, which
   * lives on the game frame next to the scoreboard rather than on this panel.
   */
  onChangeRound?: () => void;
}

/**
 * The rules panel, rebuilt in the DOM from the CreateJS `rulesclip` symbol.
 *
 * In the canvas build the heading and body are outlined vector artwork with no
 * underlying strings; here they are real text, so they are selectable and
 * reachable by assistive technology.
 */
export function VifRulesPanel({
  copy,
  round,
  roundsInModule,
  layer,
  layersInGame,
  secondsLeft,
  canStart,
  onStart,
  onChangeRound,
}: VifRulesPanelProps) {
  return (
    <aside className="vif-rules-panel">
      <span className="vif-rules-panel__badge" aria-hidden="true">
        {BADGE_GLYPH[copy.operation]}
      </span>

      {'title' in copy ? (
        <>
          <h2 className="vif-rules-panel__title">{copy.title}</h2>
          <p className="vif-rules-panel__subtitle">{copy.subtitle}</p>
          <p className="vif-rules-panel__body">{copy.body}</p>
          {copy.example ? (
            <p className="vif-rules-panel__example">{copy.example}</p>
          ) : null}
        </>
      ) : null}

      <div className="vif-rules-panel__meters">
        <VifMeter label="round" value={round} total={roundsInModule} />
        <VifMeter label="game" value={layer} total={layersInGame} />
      </div>

      {canStart ? (
        <button
          type="button"
          className="vif-rules-panel__start"
          onClick={onStart}
        >
          START
        </button>
      ) : secondsLeft === null ? null : (
        <p
          className="vif-rules-panel__clock"
          role="timer"
          aria-label={`${secondsLeft} seconds remaining`}
        >
          {secondsLeft}
        </p>
      )}

      {onChangeRound ? (
        <button
          type="button"
          className="vif-rules-panel__change-round"
          onClick={onChangeRound}
        >
          Change Round
        </button>
      ) : null}
    </aside>
  );
}

interface VifMeterProps {
  label: string;
  value: number;
  total: number;
}

/**
 * A vertical progress bar. The canvas equivalents (`gmeter` / `rmeter`) are
 * frame-based clips; filled segments are rendered here instead.
 */
function VifMeter({ label, value, total }: VifMeterProps) {
  const segments = Math.max(total, 1);
  // The current value counts as filled - the canvas meters advance to
  // `3 + intLayer` / `3 + round` on entry, so layer 1 and round 1 already
  // show a segment. Subtracting one here left both meters blank for the whole
  // of the first round.
  const filled = clamp(value, [0, segments]);

  return (
    <div className="vif-meter">
      <span className="vif-meter__label">{label}</span>
      <div
        className="vif-meter__track"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={segments}
        aria-valuenow={filled}
      >
        {Array.from({ length: segments }, (_, index) => (
          <span
            key={index}
            className={
              index < filled
                ? 'vif-meter__cell vif-meter__cell--on'
                : 'vif-meter__cell'
            }
          />
        ))}
      </div>
    </div>
  );
}
